/**
 * Email validation utility
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Check if email is unique among existing users
 */
export const isEmailUnique = (email, existingUsers, excludeEmail = null) => {
  return !existingUsers.some(
    (user) =>
      user.email.toLowerCase() === email.toLowerCase() &&
      user.email !== excludeEmail
  );
};

/**
 * Validate user form data
 */
export const validateUserForm = (
  formData,
  existingUsers = [],
  isEditing = false
) => {
  const errors = {};

  // Employee ID validation
  if (!formData.employeeId?.trim()) {
    errors.employeeId = "מספר עובד נדרש";
  }

  // Email validation
  if (!formData.email?.trim()) {
    errors.email = "אימייל נדרש";
  } else if (!validateEmail(formData.email)) {
    errors.email = "פורמט אימייל לא תקין";
  } else if (
    !isEmailUnique(
      formData.email,
      existingUsers,
      isEditing ? formData.originalEmail : null
    )
  ) {
    errors.email = "אימייל זה כבר קיים במערכת";
  }

  // Permissions validation
  if (!formData.permissions?.trim()) {
    errors.permissions = "הרשאות נדרשות";
  }

  // Password validation (only for new users)
  if (!isEditing && !formData.password?.trim()) {
    errors.password = "סיסמה נדרשת";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Format Firestore timestamp to Hebrew date string
 */
export const formatFirestoreDate = (timestamp) => {
  if (!timestamp) return "לא זמין";

  // If it's a Firestore Timestamp object
  if (timestamp.seconds) {
    return new Date(timestamp.seconds * 1000).toLocaleString("he-IL");
  }

  // If it's already a Date object
  if (timestamp instanceof Date) {
    return timestamp.toLocaleString("he-IL");
  }

  // If it's a string, try to parse it
  try {
    return new Date(timestamp).toLocaleString("he-IL");
  } catch (error) {
    return "לא זמין";
  }
};

// Cache today's date at midnight to avoid recalculating on every call
let cachedToday = null;
let cachedTodayTime = null;

/**
 * Get today's date at midnight (cached for performance)
 * @returns {number} Timestamp of today at 00:00:00
 */
const getTodayAtMidnight = () => {
  const now = Date.now();
  // Cache for 1 hour to avoid unnecessary recalculations
  if (!cachedTodayTime || now - cachedTodayTime > 3600000) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    cachedToday = today.getTime();
    cachedTodayTime = now;
  }
  return cachedToday;
};

/**
 * Calculate days since last seen and get color class
 * Optimized to minimize Date object creation
 * @param {Object|Date|string} seenAt - Timestamp of when item was last seen
 * @param {number} greenMax - Maximum days for green status (default: 2)
 * @param {number} yellowDays - Days for yellow status (default: 3)
 * @returns {Object} { days: number, colorClass: string }
 */
export const getSeenStatus = (seenAt, greenMax = 2, yellowDays = 3) => {
  if (!seenAt) {
    return { days: null, colorClass: "seen-no-data" };
  }

  let seenTimestamp;

  // Handle Firestore Timestamp (most common case)
  if (seenAt.seconds) {
    seenTimestamp = seenAt.seconds * 1000;
  } else if (seenAt instanceof Date) {
    seenTimestamp = seenAt.getTime();
  } else if (typeof seenAt === "number") {
    seenTimestamp = seenAt;
  } else {
    try {
      seenTimestamp = new Date(seenAt).getTime();
      if (isNaN(seenTimestamp)) {
        return { days: null, colorClass: "seen-no-data" };
      }
    } catch (error) {
      return { days: null, colorClass: "seen-no-data" };
    }
  }

  // Calculate days difference using timestamps (more efficient)
  const todayAtMidnight = getTodayAtMidnight();
  const seenDate = new Date(seenTimestamp);
  seenDate.setHours(0, 0, 0, 0);
  const seenAtMidnight = seenDate.getTime();

  const diffTime = todayAtMidnight - seenAtMidnight;
  const diffDays = Math.floor(diffTime / 86400000); // 1000 * 60 * 60 * 24 = 86400000

  // Determine color class based on days with custom thresholds
  let colorClass;
  if (diffDays <= greenMax) {
    colorClass = "seen-green";
  } else if (diffDays === yellowDays) {
    colorClass = "seen-yellow";
  } else {
    colorClass = "seen-red";
  }

  return { days: diffDays, colorClass };
};

/**
 * Eye button: clear seenAt when still in the green window (undo mistaken mark).
 */
export const shouldClearSeenOnEyeClick = (
  seenAt,
  greenMax = 2,
  yellowDays = 3,
) => {
  if (!seenAt) return false;
  const status = getSeenStatus(seenAt, greenMax, yellowDays);
  return status.days != null && status.days <= greenMax;
};
